import { Component } from '@theme/component';
import { WishlistManager } from '@theme/wishlist';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

// ─── WishlistPageComponent ───────────────────────────────────────────────────
// Fetches product data for each wishlisted product ID via the Storefront API
// and renders them into the product grid. Listens for wishlist updates to
// remove de-wishlisted cards without a full page reload.

class WishlistPageComponent extends Component {
  #abortController = null;
  #renderedIds = new Set();
  #fetchFailed = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    this.addEventListener('click', this.#onGridClick);
    this.#renderAll();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    this.removeEventListener('click', this.#onGridClick);
    this.#abortController?.abort();
  }

  get #grid() {
    return this.querySelector('[ref="productGrid"]');
  }

  get #emptyState() {
    return this.querySelector('[ref="emptyState"]');
  }

  get #errorState() {
    return this.querySelector('[ref="errorState"]');
  }

  get #loadingState() {
    return this.querySelector('[ref="loadingState"]');
  }

  async #renderAll() {
    this.#fetchFailed = false;
    const products = WishlistManager.getProducts();
    if (products.length === 0) {
      this.#resolveStates();
      return;
    }

    this.#setLoading(true);
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      await Promise.all(
        products
          .filter((p) => !this.#renderedIds.has(p.id))
          .map((p) => this.#fetchAndRenderProduct(p))
      );
    } finally {
      this.#setLoading(false);
      this.#resolveStates();
    }
  }

  // Fetch via Shopify's unauthenticated AJAX API. Same-origin, no token,
  // no Storefront API setup required. Items without a handle (legacy
  // localStorage entries) are skipped — they get backfilled the next time
  // the user visits the corresponding PDP/card.
  async #fetchAndRenderProduct(item) {
    if (!item?.handle) return;

    try {
      const res = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) {
        // 404 = product unpublished/deleted: skip silently, don't flag the
        // whole page as failed for one missing item.
        if (res.status !== 404) this.#fetchFailed = true;
        return;
      }

      const product = await res.json();
      if (!product || !product.id) {
        this.#fetchFailed = true;
        return;
      }

      const primaryVariant = this.#getPrimaryVariant(product);
      const ajaxInventoryQty = this.#getVariantInventoryQty(primaryVariant);
      const storefrontInventoryQty = ajaxInventoryQty ?? await this.#fetchInventoryQty(primaryVariant?.id);
      const inventoryQty = storefrontInventoryQty ?? await this.#fetchRenderedVariantInventoryQty(item.handle, primaryVariant?.id);
      this.#appendProductCard(item.id, product, inventoryQty);
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.#fetchFailed = true;
        console.warn('[wishlist-page]', err);
      }
    }
  }

  // Use Storefront inventory when client-side product data does not expose an
  // exact variant quantity.
  async #fetchInventoryQty(variantId = null) {
    const { storefrontToken, storefrontUrl } = Theme.wishlist;
    if (!storefrontToken || !storefrontUrl || !variantId) return null;
    try {
      const res = await fetch(storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({
          query: `{ node(id: "gid://shopify/ProductVariant/${String(variantId)}") { ... on ProductVariant { quantityAvailable } } }`,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.data?.node?.quantityAvailable ?? null;
    } catch {
      return null;
    }
  }

  #getPrimaryVariant(product) {
    return product?.variants?.find((variant) => variant.available) || product?.variants?.[0] || null;
  }

  #getVariantInventoryQty(variant) {
    if (variant?.inventory_management !== 'shopify') return null;
    const quantity = Number(variant.inventory_quantity);
    return Number.isFinite(quantity) ? quantity : null;
  }

  async #fetchRenderedVariantInventoryQty(handle, variantId = null) {
    if (!handle || !variantId) return null;
    try {
      const res = await fetch(
        `/products/${encodeURIComponent(handle)}?section_id=section-rendering-product-card`,
        {
          signal: this.#abortController.signal,
          headers: { Accept: 'text/html' },
        }
      );
      if (!res.ok) return null;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const variantJson = doc.querySelector('[data-wishlist-variant-inventory]')?.textContent
        || doc.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!variantJson) return null;

      const variant = JSON.parse(variantJson);
      if (String(variant.id) !== String(variantId)) return null;
      return this.#getVariantInventoryQty(variant);
    } catch {
      return null;
    }
  }

  #appendProductCard(productId, product, inventoryQty = null) {
    if (this.#renderedIds.has(productId)) return;
    this.#renderedIds.add(productId);

    const grid = this.#grid;
    if (!grid) return;

    const card = document.createElement('div');
    card.className = 'wishlist-page__item';
    card.dataset.productId = productId;
    card.innerHTML = this.#cardTemplate(product, productId, inventoryQty);
    grid.appendChild(card);
  }

  #formatMoney(cents) {
    if (cents == null) return '';
    const amount = Number(cents) / 100;
    const currency = Theme.wishlist.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  #sizedImageUrl(url, width) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  #cardTemplate(product, productId, inventoryQty = null) {
    // Shape comes from /products/{handle}.js — prices in cents, image as a
    // protocol-relative CDN URL, `available` boolean.
    const formattedPrice = this.#formatMoney(product.price);
    const imgUrl = product.featured_image || product.images?.[0] || '';
    const imgAlt = product.title;
    const primaryVariant = this.#getPrimaryVariant(product);
    const available = product.available !== false && primaryVariant?.available === true;
    const variantId = primaryVariant?.id;
    const removeText = Theme.wishlist.translations.remove_from_wishlist || 'Remove from wishlist';
    const addToCartText = Theme.wishlist.translations.add_to_cart || 'Add to cart';
    const inStockText = Theme.wishlist.translations.in_stock || 'In stock';
    const outOfStockText = Theme.wishlist.translations.out_of_stock || Theme.wishlist.translations.sold_out || 'Out of stock';

    const lowStockThreshold = Number.isFinite(Number(Theme.wishlist.lowStockThreshold))
      ? Number(Theme.wishlist.lowStockThreshold)
      : 10;

    let stockBadge = '';
    if (!available) {
      stockBadge = `<span class="wishlist-card__stock wishlist-card__stock--out">${outOfStockText}</span>`;
    } else if (inventoryQty !== null && inventoryQty > 0 && inventoryQty <= lowStockThreshold) {
      const msg = (Theme.wishlist.translations.low_stock_count || 'Only {{ count }} left!')
        .replace('{{ count }}', inventoryQty);
      stockBadge = `<span class="wishlist-card__stock wishlist-card__stock--low">${msg}</span>`;
    } else {
      stockBadge = `<span class="wishlist-card__stock wishlist-card__stock--in">${inStockText}</span>`;
    }

    return `
      <div class="wishlist-card">
        <a href="/products/${product.handle}" class="wishlist-card__link">
          <div class="wishlist-card__image-wrapper">
            ${
              imgUrl
                ? `<img src="${this.#sizedImageUrl(imgUrl, 400)}" alt="${imgAlt}" class="wishlist-card__image" loading="lazy" width="400" height="400">`
                : `<div class="wishlist-card__image-placeholder"></div>`
            }
          </div>
          <div class="wishlist-card__info">
            <p class="wishlist-card__title">${product.title}</p>
            <div class="wishlist-card__price-row">
              <p class="wishlist-card__price">${formattedPrice}</p>
              ${stockBadge}
            </div>
          </div>
        </a>
        <div class="wishlist-card__footer">
          ${available && variantId ? `
            <button class="button wishlist-card__btn--add-to-cart"
              data-variant-id="${variantId}"
              data-product-id="${productId}">
              ${addToCartText}
            </button>
          ` : ''}
          <button class="button-secondary wishlist-card__btn--remove"
            data-product-id="${productId}">
            ${removeText}
          </button>
        </div>
      </div>
    `;
  }

  #onGridClick = (e) => {
    const removeBtn = e.target.closest('.wishlist-card__btn--remove');
    if (removeBtn) {
      WishlistManager.remove(removeBtn.dataset.productId);
      return;
    }
    const atcBtn = e.target.closest('.wishlist-card__btn--add-to-cart');
    if (atcBtn) this.#addToCart(atcBtn, atcBtn.dataset.variantId);
  };

  async #addToCart(button, variantId) {
    const addToCartText = Theme.wishlist.translations.add_to_cart || 'Add to cart';
    const addingText = Theme.wishlist.translations.adding_to_cart || 'Adding…';
    const addedText = Theme.wishlist.translations.added_to_cart || 'Added!';
    const errorText = Theme.wishlist.translations.add_to_cart_error || 'Error';

    button.disabled = true;
    button.textContent = addingText;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      if (res.ok) {
        const cart = await res.json();
        button.textContent = addedText;
        document.dispatchEvent(new CartUpdateEvent(cart, button.dataset.productId, { variantId }));
        WishlistManager.remove(button.dataset.productId);
      } else {
        button.textContent = errorText;
      }
    } catch {
      button.textContent = errorText;
    } finally {
      setTimeout(() => {
        button.textContent = addToCartText;
        button.disabled = false;
      }, 1500);
    }
  }

  #onWishlistUpdate = () => {
    const currentProducts = WishlistManager.getProducts();
    const currentIds = new Set(currentProducts.map((p) => p.id));
    const grid = this.#grid;
    if (!grid) return;

    // Remove cards no longer in the wishlist
    grid.querySelectorAll('[data-product-id]').forEach((el) => {
      if (!currentIds.has(el.dataset.productId)) {
        el.remove();
        this.#renderedIds.delete(el.dataset.productId);
      }
    });

    // Fetch cards for newly added items
    const newItems = currentProducts.filter((p) => !this.#renderedIds.has(p.id));
    if (newItems.length > 0) {
      this.#fetchFailed = false;
      Promise.all(newItems.map((item) => this.#fetchAndRenderProduct(item))).then(
        () => this.#resolveStates()
      );
    } else {
      this.#resolveStates();
    }
  };

  #resolveStates() {
    const empty = this.#emptyState;
    const error = this.#errorState;
    const products = WishlistManager.getProducts();
    const hasItems = products.length > 0;
    const hasFetchable = products.some((p) => p.handle);
    const hasRendered = this.#renderedIds.size > 0;

    // Show empty state only when there are truly no wishlisted items.
    if (empty) empty.classList.toggle('hidden', hasItems);

    // Show error only when fetchable items exist but nothing rendered.
    // Legacy items without handles aren't an error — they backfill on the
    // next PDP visit.
    if (error) error.classList.toggle('hidden', !(hasFetchable && !hasRendered && this.#fetchFailed));
  }

  #setLoading(loading) {
    const el = this.#loadingState;
    if (el) el.classList.toggle('hidden', !loading);
  }
}

if (!customElements.get('wishlist-page-component')) {
  customElements.define('wishlist-page-component', WishlistPageComponent);
}
